// A13 — API kalitlari va dasturchilar. Module-local data layer (does NOT touch
// `lib/adapters.ts` / `i18n/*` — C-session HOT). Backend `/admin/developers/*`
// greenfield → fetchers fail-soft to empty; mutations POST when it ships.
// Registration = ./REGISTER.md (C applies routes/screenStatus/nav/i18n/perm).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(
  rows: T[],
  params: FetchParams,
  search?: (r: T) => string,
): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/** A13.01 — API application (Telegram-style api_id app). */
export interface AppRow {
  id: number
  name: string
  api_id: number
  owner: string
  status: 'active' | 'blocked'
  requests_today: number
  created_at: string
}

/** A13.07 — a bot token under monitoring. */
export interface BotTokenRow {
  id: number
  bot_username: string
  owner: string
  last_used: string | null
  requests: number
  revoked: boolean
}

/** A13.08 — a token found leaked by the GitHub/GitLab secret scanner. */
export interface LeakedTokenRow {
  id: number
  token_masked: string
  kind: 'bot' | 'api' | 'webhook'
  source: string
  found_at: string
  status: 'open' | 'revoked' | 'ignored'
}

export function fetchApps(params: FetchParams): Promise<FetchResult<AppRow>> {
  return safeList<AppRow>('/admin/developers/apps', 'apps', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.owner}`),
  )
}

/** POST /admin/developers/apps/:id/status — active|blocked (A13.05). */
export function setAppStatus(id: number, status: string): Promise<{ status: string }> {
  return api.post<{ status: string }>(`/admin/developers/apps/${id}/status`, { status })
}

export function fetchBotTokens(params: FetchParams): Promise<FetchResult<BotTokenRow>> {
  return safeList<BotTokenRow>('/admin/developers/bot-tokens', 'tokens', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.bot_username} ${r.owner}`),
  )
}

/** POST /admin/developers/bot-tokens/:id/revoke — forced revoke (A13.09). */
export function revokeBotToken(id: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/developers/bot-tokens/${id}/revoke`, {})
}

export function fetchLeaked(params: FetchParams): Promise<FetchResult<LeakedTokenRow>> {
  return safeList<LeakedTokenRow>('/admin/developers/leaked', 'entries', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.source} ${r.kind}`),
  )
}

/** POST /admin/developers/leaked/:id/:action — revoke|ignore (A13.08). */
export function resolveLeaked(id: number, action: 'revoke' | 'ignore'): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/developers/leaked/${id}/${action}`, {})
}

/** A13.16 — an outgoing webhook + its signing secret (masked). */
export interface WebhookRow {
  id: number
  url: string
  event: string
  secret_masked: string
  last_delivery: string | null
  failures: number
}

/** A13.18 — a registered OAuth application. */
export interface OAuthAppRow {
  id: number
  name: string
  client_id: string
  owner: string
  scopes: string
  status: 'active' | 'revoked'
}

/** A13.04 — a rate-limit tier. */
export interface RateLimitRow {
  id: number
  tier: string
  per_minute: number
  burst: number
  applies_to: string
}

export function fetchWebhooks(params: FetchParams): Promise<FetchResult<WebhookRow>> {
  return safeList<WebhookRow>('/admin/developers/webhooks', 'webhooks', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.url} ${r.event}`),
  )
}

/** POST /admin/developers/webhooks/:id/rotate — new signing secret (A13.16). */
export function rotateWebhook(id: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/developers/webhooks/${id}/rotate`, {})
}

export function fetchOAuthApps(params: FetchParams): Promise<FetchResult<OAuthAppRow>> {
  return safeList<OAuthAppRow>('/admin/developers/oauth', 'apps', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.owner}`),
  )
}

/** POST /admin/developers/oauth/:id/revoke (A13.18). */
export function revokeOAuthApp(id: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/developers/oauth/${id}/revoke`, {})
}

export function fetchRateLimits(params: FetchParams): Promise<FetchResult<RateLimitRow>> {
  return safeList<RateLimitRow>('/admin/developers/rate-limits', 'tiers', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.tier} ${r.applies_to}`),
  )
}

/** Module-local RU strings (i18n HOT → keyга ko'chiriladi, REGISTER.md). */
export const DEV_STR = {
  apps: 'API-приложения',
  botTokens: 'Мониторинг токенов ботов',
  leaked: 'Утёкшие токены',
  name: 'Название',
  apiId: 'api_id',
  owner: 'Владелец',
  status: 'Статус',
  requestsToday: 'Запросов сегодня',
  created: 'Создан',
  bot: 'Бот',
  lastUsed: 'Последнее использование',
  requests: 'Запросов',
  kind: 'Тип',
  source: 'Источник',
  foundAt: 'Найден',
  block: 'Заблокировать',
  unblock: 'Разблокировать',
  revoke: 'Отозвать',
  ignore: 'Игнорировать',
  statusChanged: 'Статус обновлён',
  revoked: 'Токен отозван',
  ignored: 'Отмечено как игнорируемое',
  failed: 'Не удалось выполнить',
  // A13.16 webhooks
  webhooks: 'Webhook-секреты',
  url: 'URL',
  event: 'Событие',
  secret: 'Секрет',
  lastDelivery: 'Последняя доставка',
  failures: 'Ошибок',
  rotate: 'Сменить секрет',
  rotated: 'Секрет обновлён',
  // A13.18 oauth
  oauth: 'OAuth-приложения',
  clientId: 'client_id',
  scopes: 'Права (scopes)',
  revokeApp: 'Отозвать',
  appRevoked: 'Приложение отозвано',
  // A13.04 rate limits
  rateLimits: 'Лимиты запросов',
  tier: 'Тариф',
  perMinute: 'Запросов/мин',
  burst: 'Всплеск',
  appliesTo: 'Применяется к',
} as const
